// Thin sub-page wrappers for /settings/* deep-links. Each one renders the
// existing tab component inside the shared SettingsPageShell so the back
// link + heading are consistent.
import SettingsPageShell from "./SettingsPageShell";
import GeneralTab from "../../components/GeneralTab";
import DataTab from "../../components/DataTab";
import AuditLogTab from "../../components/AuditLogTab";
import TemplatesTab from "../../components/TemplatesTab";
import MessageTemplatesTab from "../../components/MessageTemplatesTab";
import { invalidateWizardCatalogCache } from "../../lib/catalogCache";
import {
  StoresTab, UsersTab, PrintingTab, TyroIntegrationCard, SmsDeliveryLogCard,
} from "../Settings";

export function GeneralSettingsPage() {
  return (
    <SettingsPageShell title="General" description="Business identity, tax mode, surcharge defaults, loyalty and rounding rules." testid="page-settings-general">
      <GeneralTab />
    </SettingsPageShell>
  );
}

export function StoresSettingsPage() {
  return (
    <SettingsPageShell title="Stores" description="Add, edit and remove your physical branches. Each store has its own till, printers, EFTPOS and SMS settings." testid="page-settings-stores">
      <StoresTab />
    </SettingsPageShell>
  );
}

export function UsersSettingsPage() {
  return (
    <SettingsPageShell title="Staff & users" description="Invite staff, assign roles (admin / staff) and reset passwords or PINs." testid="page-settings-users">
      <UsersTab />
    </SettingsPageShell>
  );
}

export function PrintersSettingsPage() {
  return (
    <SettingsPageShell title="Printers — local" description="Set the default Epson thermal receipt printer and Dymo label printer for this store, plus paper widths." testid="page-settings-printers">
      <PrintingTab />
    </SettingsPageShell>
  );
}

export function TemplatesSettingsPage() {
  return (
    <SettingsPageShell title="Templates" description="Customise receipt, label and email copy. Use merge tags like {{customer.name}} and {{store.phone}}." testid="page-settings-templates">
      <TemplatesTab />
    </SettingsPageShell>
  );
}

export function MessagesSettingsPage() {
  return (
    <SettingsPageShell title="SMS & Email templates" description="Edit the wording customers receive. Details like name, ticket and amount fill in automatically." testid="page-settings-messages">
      <MessageTemplatesTab />
    </SettingsPageShell>
  );
}

export function DataSettingsPage() {
  return (
    <SettingsPageShell title="Data" description="Recover from bad imports, export your database or wipe untransacted records." testid="page-settings-data">
      <DataTab />
    </SettingsPageShell>
  );
}

export function AuditSettingsPage() {
  return (
    <SettingsPageShell title="Audit log" description="Every destructive admin action — voids, refunds, deletions and config changes." testid="page-settings-audit">
      <AuditLogTab />
    </SettingsPageShell>
  );
}

export function TyroSettingsPage() {
  return (
    <SettingsPageShell
      title="Tyro EFTPOS"
      overline="PAYMENTS · TYRO"
      description="Pair a Tyro Pair@POS terminal, configure surcharge, integrated receipts and run the Phase 1 certification scenarios."
      testid="page-settings-tyro"
    >
      <div className="p-6 max-w-5xl">
        <TyroIntegrationCard />
      </div>
    </SettingsPageShell>
  );
}

export function SmsSettingsPage() {
  return (
    <SettingsPageShell
      title="SMS — Twilio"
      overline="MESSAGING · TWILIO"
      description="Outbound SMS for repair status updates, sale receipts and marketing. Configure Twilio credentials in backend .env. Delivery log below shows recent sends."
      testid="page-settings-sms"
    >
      <div className="p-6 max-w-5xl space-y-6">
        <EnvKeyHint
          provider="Twilio"
          docUrl="https://www.twilio.com/console"
          envKeys={["TWILIO_ACCOUNT_SID", "TWILIO_AUTH_TOKEN", "TWILIO_FROM"]}
          tips={[
            "Australian A2P 10DLC registration is required for marketing SMS.",
            "Use a Twilio Messaging Service SID instead of TWILIO_FROM if rotating numbers.",
          ]}
        />
        <SmsDeliveryLogCard />
      </div>
    </SettingsPageShell>
  );
}

export function EmailSettingsPage() {
  return (
    <SettingsPageShell
      title="Email — Resend"
      overline="MESSAGING · RESEND"
      description="Transactional email (PDF receipts, repair status, password reset) is sent via Resend. Add the API key in backend .env."
      testid="page-settings-email"
    >
      <div className="p-6 max-w-5xl space-y-6">
        <EnvKeyHint
          provider="Resend"
          docUrl="https://resend.com/api-keys"
          envKeys={["RESEND_API_KEY", "RESEND_FROM_EMAIL"]}
          tips={[
            "Verify your sending domain in Resend before going live.",
            "RESEND_FROM_EMAIL must use an address on your verified domain (e.g. receipts@irepair.com.au).",
          ]}
        />
      </div>
    </SettingsPageShell>
  );
}

export function PrintNodeSettingsPage() {
  return (
    <SettingsPageShell
      title="PrintNode — cloud printing"
      overline="DEVICES · PRINTNODE"
      description="Route receipts and labels to any networked printer (Epson, Dymo, Brother) via PrintNode."
      testid="page-settings-printnode"
    >
      <div className="p-6 max-w-5xl space-y-6">
        <EnvKeyHint
          provider="PrintNode"
          docUrl="https://app.printnode.com/account/apikeys"
          envKeys={["PRINTNODE_API_KEY"]}
          tips={[
            "Install the PrintNode client on the store PC and pair it with your account.",
            "Per-store default printers are set under Settings → Printers.",
          ]}
        />
        <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 text-[12px] text-[#4B5563] leading-relaxed">
          Local printer defaults (Epson receipt + Dymo label) are configured under{" "}
          <a href="/settings/printers" className="text-[#1E5FEB] hover:underline">Printers</a>.
        </div>
      </div>
    </SettingsPageShell>
  );
}

export function GooglePlacesSettingsPage() {
  return (
    <SettingsPageShell
      title="Google Places"
      overline="SYSTEM · GOOGLE"
      description="Address autocomplete for the booking widget and customer profile. Falls back to OpenStreetMap (Nominatim) when no key is set."
      testid="page-settings-google"
    >
      <div className="p-6 max-w-5xl space-y-6">
        <EnvKeyHint
          provider="Google Cloud"
          docUrl="https://console.cloud.google.com/apis/credentials"
          envKeys={["GOOGLE_PLACES_API_KEY"]}
          tips={[
            "Enable Places API (New) for the project.",
            "Restrict the key to your domain + the Places API for safety.",
          ]}
        />
      </div>
    </SettingsPageShell>
  );
}

export function BookingWebhookSettingsPage() {
  // Re-uses the booking webhook section already present in the legacy
  // IntegrationsTab. We extract the booking-specific UI inline rather than
  // splitting it further to keep the diff small.
  return (
    <SettingsPageShell
      title="Booking webhook"
      overline="SYSTEM · BOOKINGS"
      description="Inbound bookings webhook. Use the endpoint + API key below in your booking website to push enquiries straight into the Bookings inbox."
      testid="page-settings-booking-webhook"
    >
      <BookingWebhookBlock />
    </SettingsPageShell>
  );
}

export function MigrateSettingsPage() {
  return (
    <SettingsPageShell
      title="Migrate from another POS"
      overline="DATA · MIGRATION"
      description="One-time bulk import from RepairDesk / RepairShopr exports. Customers, tickets and invoices are auto-linked by phone/email."
      testid="page-settings-migrate"
    >
      <MigrateBlock />
    </SettingsPageShell>
  );
}

export function HistoricalGrossSettingsPage() {
  return (
    <SettingsPageShell
      title="Historical revenue"
      overline="DATA · REPORTS HISTORY"
      description="Upload the RepairDesk Multi-Store Summary so Reports → All-time reflect your true business history, not just sales made in iRepair POS."
      testid="page-settings-gross"
    >
      <HistoricalGrossBlock />
    </SettingsPageShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Reusable blocks below
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { Copy, ArrowsClockwise, Database, ChartLineUp, Link as LinkIcon, Key } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import MigrateFromPOSModal from "../../components/MigrateFromPOSModal";
import ImportGrossModal from "../../components/ImportGrossModal";

function EnvKeyHint({ provider, envKeys, docUrl, tips }) {
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#F3F4F6] flex items-center justify-center shrink-0">
          <Key size={20} weight="duotone" className="text-[#4B5563]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
            Configuration
          </div>
          <div className="mt-1 font-display text-[18px] font-semibold tracking-tight text-[#121216]">
            {provider} credentials
          </div>
          <p className="text-[12px] text-[#4B5563] mt-2 leading-relaxed">
            Add the following keys to your <code className="font-mono text-[11px] bg-[#F8FAFC] px-1 rounded">backend/.env</code> file, then restart the backend service.
            {" "}
            <a className="text-[#1E5FEB] hover:underline" href={docUrl} target="_blank" rel="noreferrer">Get your {provider} API key →</a>
          </p>
          <div className="mt-4 space-y-2">
            {envKeys.map((k) => (
              <div key={k} className="flex items-center justify-between gap-3 rounded-lg border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2">
                <code className="font-mono text-[12px] text-[#121216]">{k}=</code>
                <span className="text-[10px] text-[#9CA3AF]">backend/.env</span>
              </div>
            ))}
          </div>
          {tips?.length > 0 && (
            <ul className="mt-4 text-[12px] text-[#4B5563] leading-relaxed space-y-1.5 list-disc pl-5">
              {tips.map((t, i) => <li key={i}>{t}</li>)}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function BookingWebhookBlock() {
  const [data, setData] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [rotating, setRotating] = useState(false);

  const load = () => api.get("/integrations/booking").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Clipboard unavailable"); }
  };

  const rotate = async () => {
    if (!window.confirm("Rotate the Booking API key? The old key will stop working immediately.")) return;
    setRotating(true);
    try { await api.post("/integrations/booking/rotate"); toast.success("Key rotated"); setRevealed(true); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setRotating(false); }
  };

  if (!data) return <div className="p-6 text-sm text-[#9CA3AF]">Loading…</div>;

  const maskedKey = data.api_key ? `${data.api_key.slice(0,4)}${"•".repeat(Math.max(0, data.api_key.length - 8))}${data.api_key.slice(-4)}` : "";
  const sampleJson = JSON.stringify(data.sample_payload, null, 2);
  const curlExample = `curl -X POST "${data.endpoint}" \\
  -H "Content-Type: application/json" \\
  -H "X-Booking-Key: ${data.api_key}" \\
  -d '${JSON.stringify(data.sample_payload)}'`;

  return (
    <div className="p-6 max-w-4xl space-y-6">
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6 space-y-4">
        <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">Endpoint</div>
        <KVRow label="Endpoint URL" value={data.endpoint} testid="booking-endpoint" onCopy={() => copy(data.endpoint, "Endpoint URL")} icon={<LinkIcon size={14} weight="bold" />} />
        <KVRow label="Header name"  value={data.header_name} testid="booking-header" onCopy={() => copy(data.header_name, "Header name")} monospace />
        <KVRow
          label="API key"
          value={revealed ? data.api_key : maskedKey}
          testid="booking-api-key"
          onCopy={() => copy(data.api_key, "API key")}
          monospace
          trailing={
            <>
              <button onClick={() => setRevealed((v) => !v)} className="text-[11px] px-2.5 py-1 rounded-full border border-[#E5E7EB] hover:border-[#121216] text-[#4B5563] hover:text-[#121216] font-medium" data-testid="booking-reveal-key">
                {revealed ? "Hide" : "Show"}
              </button>
              <button onClick={rotate} disabled={rotating} className="text-[11px] px-2.5 py-1 rounded-full border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white font-medium flex items-center gap-1" data-testid="booking-rotate-key">
                <ArrowsClockwise size={11} weight="bold" /> Rotate
              </button>
            </>
          }
        />
      </section>

      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
        <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-3">Sample payload</div>
        <pre className="bg-[#0F172A] text-[#E0E7FF] font-mono text-[11px] leading-relaxed p-4 rounded-xl overflow-auto whitespace-pre" data-testid="booking-sample-json">{sampleJson}</pre>
        <button onClick={() => copy(sampleJson, "Sample payload")} className="mt-3 h-9 px-3 rounded-full border border-[#E5E7EB] hover:border-[#121216] text-[12px] text-[#4B5563] hover:text-[#121216] font-medium flex items-center gap-2">
          <Copy size={12} weight="bold" /> Copy JSON
        </button>
      </section>

      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
        <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-3">cURL example</div>
        <pre className="bg-[#0F172A] text-[#E0E7FF] font-mono text-[11px] leading-relaxed p-4 rounded-xl overflow-auto whitespace-pre" data-testid="booking-curl">{curlExample}</pre>
        <button onClick={() => copy(curlExample, "cURL")} className="mt-3 h-9 px-3 rounded-full border border-[#E5E7EB] hover:border-[#121216] text-[12px] text-[#4B5563] hover:text-[#121216] font-medium flex items-center gap-2">
          <Copy size={12} weight="bold" /> Copy cURL
        </button>
      </section>
    </div>
  );
}

function KVRow({ label, value, monospace, onCopy, trailing, testid, icon }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2.5" data-testid={testid}>
      <div className="flex items-center gap-2 min-w-0">
        {icon && <span className="text-[#9CA3AF]">{icon}</span>}
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.12em] uppercase text-[#9CA3AF] font-medium">{label}</div>
          <div className={`mt-0.5 text-[12px] text-[#121216] truncate ${monospace ? "font-mono" : ""}`}>{value}</div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {trailing}
        <button onClick={onCopy} className="text-[#4B5563] hover:text-[#121216] p-1.5 rounded-md hover:bg-white">
          <Copy size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function MigrateBlock() {
  const [open, setOpen] = useState(false);
  return (
    <div className="p-6 max-w-4xl">
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#DCFCE7] flex items-center justify-center shrink-0">
            <Database size={20} weight="duotone" className="text-[#2F8F6E]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">One-time migration</div>
            <div className="mt-1 font-display text-[18px] font-semibold tracking-tight text-[#121216]">
              Import from RepairDesk / RepairShopr
            </div>
            <p className="text-[12px] text-[#4B5563] mt-2 leading-relaxed">
              Upload a <code className="font-mono text-[11px] bg-[#F8FAFC] px-1 rounded">.xlsx</code> export with{" "}
              <strong>Customers</strong> / <strong>Tickets</strong> / <strong>Invoices</strong> sheets. We auto-detect sheets, auto-map columns and link tickets &amp; invoices to customers by phone/email.
            </p>
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="open-migrate-modal"
            className="h-10 px-4 rounded-full bg-[#121216] hover:bg-[#1E5FEB] text-white text-[12px] font-semibold flex items-center gap-2 transition-colors"
          >
            <Database size={13} weight="bold" /> Start migration
          </button>
        </div>
      </section>
      <MigrateFromPOSModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function HistoricalGrossBlock() {
  const [open, setOpen] = useState(false);
  return (
    <div className="p-6 max-w-4xl">
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#FEF3C7] flex items-center justify-center shrink-0">
            <ChartLineUp size={20} weight="duotone" className="text-[#B45309]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">Reports history</div>
            <div className="mt-1 font-display text-[18px] font-semibold tracking-tight text-[#121216]">
              Import RepairDesk historical gross
            </div>
            <p className="text-[12px] text-[#4B5563] mt-2 leading-relaxed">
              Upload the <strong>Multi-Store Summary</strong> XLSX from RepairDesk to add a pre-migration revenue baseline per store. These totals get folded into Reports so &ldquo;All time&rdquo; / &ldquo;Last year&rdquo; reflect your true business history.
            </p>
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="open-gross-modal"
            className="h-10 px-4 rounded-full bg-[#121216] hover:bg-[#B45309] text-white text-[12px] font-semibold flex items-center gap-2 transition-colors"
          >
            <ChartLineUp size={13} weight="bold" /> Upload summary
          </button>
        </div>
      </section>
      <ImportGrossModal open={open} onClose={() => setOpen(false)} onImported={() => {}} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Website Catalog Sync — pull manufacturers/models/repairs/part-options
// with prices from the irepairexperts.com.au public catalog API. Nightly
// scheduler runs at 02:00 AEST; "Sync now" is a manual admin override.
// ─────────────────────────────────────────────────────────────────────────
export function WebsiteCatalogSyncPage() {
  return (
    <SettingsPageShell
      title="Website catalog sync"
      description="Pull your live catalog (manufacturers → models → repairs → part options + prices) from the website into POS every night. Local additions and price overrides are preserved."
      testid="page-settings-website-catalog"
    >
      <WebsiteCatalogSyncBlock />
    </SettingsPageShell>
  );
}

function WebsiteCatalogSyncBlock() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/catalog/sync/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const runSync = async (force = false) => {
    setRunning(true); setError("");
    try {
      const { data } = await api.post(`/catalog/sync${force ? "?force=true" : ""}`);
      // Invalidate the wizard's sessionStorage cache so the next ticket
      // wizard open picks up any new models/repairs immediately.
      invalidateWizardCatalogCache();
      if (data.skipped) {
        toast.success("Catalog is up to date — no changes to pull");
      } else {
        toast.success(`Sync complete · +${data.added} added · ~${data.updated} updated · —${data.archived} archived`);
      }
      load();
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  };

  if (!status) return <div className="p-6 text-sm text-[#9CA3AF]">Loading…</div>;

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const summary = status.summary || {};

  return (
    <div className="p-6 max-w-4xl space-y-6">
      {/* Connection health */}
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">Connection</div>
            <div className="mt-1 font-display text-[18px] font-semibold tracking-tight text-[#121216]">
              {status.configured ? "Configured" : "Not configured"}
            </div>
            <p className="text-[12px] text-[#4B5563] mt-1 leading-relaxed">
              {status.configured
                ? "Both CATALOG_API_URL and CATALOG_API_KEY are set. The nightly job runs at 02:00 AEST."
                : "Set CATALOG_API_URL and CATALOG_API_KEY in backend/.env, then restart the backend."}
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full text-[11px] font-semibold ${status.configured ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
            {status.configured ? "READY" : "NOT SET"}
          </div>
        </div>
      </section>

      {/* Sync state */}
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile label="Active items" value={status.totals?.active ?? 0} testid="stat-active" />
          <StatTile label="Archived" value={status.totals?.archived ?? 0} testid="stat-archived" />
          <StatTile label="Currency" value={status.currency || "AUD"} testid="stat-currency" />
          <StatTile label="Website hash" value={(status.version || "—").slice(-8)} mono testid="stat-hash" />
        </div>
        <div className="pt-4 border-t border-[#F1F5F9] grid grid-cols-1 md:grid-cols-2 gap-4 text-[12px] text-[#4B5563]">
          <div><span className="text-[#9CA3AF]">Last successful sync:</span> <span className="text-[#121216] font-medium">{fmt(status.last_synced_at)}</span></div>
          <div><span className="text-[#9CA3AF]">Last checked:</span> <span className="text-[#121216] font-medium">{fmt(status.last_checked_at)}</span></div>
          <div><span className="text-[#9CA3AF]">Website generated at:</span> <span className="text-[#121216] font-medium">{fmt(status.generated_at)}</span></div>
          <div><span className="text-[#9CA3AF]">Last summary:</span> <span className="text-[#121216] font-medium">+{summary.added || 0} ~{summary.updated || 0} —{summary.archived || 0}</span></div>
        </div>
        <div className="pt-4 border-t border-[#F1F5F9] flex flex-wrap items-center gap-3">
          <button
            data-testid="catalog-sync-now"
            onClick={() => runSync(false)}
            disabled={running || !status.configured}
            className="h-10 px-5 rounded-full bg-[#121216] hover:bg-[#B45309] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[12px] font-semibold flex items-center gap-2 transition-colors"
          >
            <ArrowsClockwise size={13} weight="bold" className={running ? "animate-spin" : ""} />
            {running ? "Syncing…" : "Sync now"}
          </button>
          <button
            data-testid="catalog-sync-force"
            onClick={() => runSync(true)}
            disabled={running || !status.configured}
            className="h-10 px-5 rounded-full border border-[#E5E7EB] hover:bg-[#F8FAFC] disabled:opacity-40 disabled:cursor-not-allowed text-[12px] font-semibold text-[#121216] transition-colors"
          >
            Force re-download
          </button>
          {error && <span className="text-[12px] text-[#B91C1C] ml-2">{error}</span>}
        </div>
      </section>

      {/* Recent price changes — audit feed */}
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">Audit</div>
            <div className="mt-1 font-display text-[16px] font-semibold tracking-tight text-[#121216]">Recent price changes</div>
          </div>
        </div>
        {(!status.recent_price_changes || status.recent_price_changes.length === 0) ? (
          <div className="text-[12px] text-[#9CA3AF] py-6 text-center">No price changes detected yet.</div>
        ) : (
          <div className="divide-y divide-[#F1F5F9] -my-2">
            {status.recent_price_changes.map((c, i) => (
              <div key={c.id || i} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[13px] text-[#121216] truncate">{c.path}</div>
                  <div className="text-[11px] text-[#9CA3AF] mt-0.5">{fmt(c.at)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[11px] text-[#9CA3AF] line-through">${c.old_price?.toFixed(2)}</div>
                  <div className="text-[13px] font-semibold text-[#121216]">${c.new_price?.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatTile({ label, value, mono, testid }) {
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3" data-testid={testid}>
      <div className="text-[10px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">{label}</div>
      <div className={`mt-1 text-[18px] font-semibold text-[#121216] ${mono ? "font-mono text-[14px]" : ""}`}>{value}</div>
    </div>
  );
}
